import { useRef } from "react";
import { NAVY } from "../styles/palette";
import { useIsMobile } from "../lib/useIsMobile";

// ═══════════════════════════════════════════════════════════════════
// CLICKABLE DATE
// ═══════════════════════════════════════════════════════════════════
// Native <input type="date"> only opens the picker when you click the
// tiny calendar icon on the right side, which is annoying. This wraps
// the input in a clickable container that calls input.showPicker() on
// any click inside the wrapper — clicking the icon, the date text,
// or the padding all open the picker.
//
// Falls back to focus() on browsers without showPicker (older Firefox).
//
// Drop-in replacement for <input type="date">. Same props you'd
// expect: value, onChange, min, max, disabled. Plus optional `icon`
// (default 📅) and `width`/`style` for layout overrides.
// ═══════════════════════════════════════════════════════════════════

export function ClickableDate({
  value,
  onChange,
  min,
  max,
  disabled,
  icon = "📅",
  width,
  style,
  title,
  placeholder,
}) {
  const inputRef = useRef(null);
  const isMobile = useIsMobile();

  function openPicker() {
    if (disabled) return;
    const el = inputRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (e) { /* fall through */ }
    }
    el.focus();
  }

  return (
    <div
      onClick={openPicker}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        // Phone: the theme gives the inner date input min-height:44px, so the chip
        // only needs slim padding to be a 48px tap target that never exceeds its cell.
        padding: isMobile ? "2px 12px" : "6px 12px",
        minHeight: isMobile ? 44 : undefined,
        border: "1px solid #dfe1e6",
        borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? "#f4f5f7" : "white",
        opacity: disabled ? 0.6 : 1,
        width,
        ...(isMobile ? { maxWidth: "100%", boxSizing: "border-box" } : {}),   // phones only: desktop callers pass content-box widths
        ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = NAVY; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#dfe1e6"; }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={e => onChange && onChange(e.target.value)}
        min={min}
        max={max}
        disabled={disabled}
        placeholder={placeholder}
        // Strip the input's own chrome so the wrapper is the visible field.
        // Note: most browsers still show the small built-in icon — we
        // keep cursor:pointer + flex:1 so the wrapper's click handler
        // still covers everywhere.
        style={{
          border: "none",
          outline: "none",
          fontSize: 13,
          fontWeight: 600,
          color: NAVY,
          background: "transparent",
          padding: 0,
          cursor: disabled ? "not-allowed" : "pointer",
          flex: 1,
          minWidth: 0,
        }}
      />
    </div>
  );
}
